import { useEffect, useLayoutEffect, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Topbar } from '@components/topbar'
import { Footer } from '@components/footer'
import { SEO } from '@components/seo'
import { useStoreActions, useStoreState } from '@stores'
import { CLIENT_CODE, SITE_URL, truncate } from '@constants'

export const DirectoryDetailPage = () => {
	const { slug } = useParams<{ slug: string }>()
	const { fetchAllOutlet } = useStoreActions(action => action.outlet)
	const { outlets } = useStoreState(state => state.outlet)

	useLayoutEffect(() => {
		window.scrollTo(0, 0)
	}, [slug])

	useEffect(() => {
		if (!outlets || outlets.length === 0) {
			fetchAllOutlet({
				page: 1,
				limit: 100,
				client_code: CLIENT_CODE
			})
		}
	}, [fetchAllOutlet, outlets])

	const outlet = useMemo(
		() => outlets?.find(o => o.slug === slug),
		[outlets, slug]
	)

	const storeJsonLd = outlet
		? {
				'@context': 'https://schema.org',
				'@type': 'Store',
				name: outlet.name,
				url: `${SITE_URL}/directory/${outlet.slug}`,
				image: outlet.image || undefined,
				description: outlet.note || undefined,
				telephone: outlet.contact_phone || undefined,
				email: outlet.contact_email || undefined,
				address: {
					'@type': 'PostalAddress',
					streetAddress: 'Jl. Raya Pajajaran',
					addressLocality: 'Bogor',
					addressRegion: 'Jawa Barat',
					postalCode: '16127',
					addressCountry: 'ID'
				},
				geo:
					outlet.latitude && outlet.longitude
						? {
								'@type': 'GeoCoordinates',
								latitude: outlet.latitude,
								longitude: outlet.longitude
							}
						: undefined,
				containedInPlace: { '@id': `${SITE_URL}/#mall` },
				category: outlet.store_category?.name || undefined
			}
		: null

	const breadcrumb = outlet
		? {
				'@context': 'https://schema.org',
				'@type': 'BreadcrumbList',
				itemListElement: [
					{ '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
					{
						'@type': 'ListItem',
						position: 2,
						name: 'Directory',
						item: `${SITE_URL}/directory`
					},
					{
						'@type': 'ListItem',
						position: 3,
						name: outlet.name,
						item: `${SITE_URL}/directory/${outlet.slug}`
					}
				]
			}
		: null

	return (
		<div className="min-h-screen bg-gray-50">
			{outlet && (
				<SEO
					title={`${outlet.name}${outlet.store_category?.name ? ` — ${outlet.store_category.name}` : ''}`}
					description={truncate(
						outlet.note ||
							`${outlet.name} di Botani Square Mall Bogor${outlet.floor ? `, lantai ${outlet.floor}` : ''}. Kunjungi sekarang.`,
						180
					)}
					path={`/directory/${outlet.slug}`}
					image={outlet.image || undefined}
					jsonLd={[storeJsonLd!, breadcrumb!]}
				/>
			)}
			<Topbar />

			<section className="pt-28 pb-8 sm:pt-32 sm:pb-12">
				<div className="max-w-screen-lg mx-auto px-4 sm:px-6">
					<Link
						to="/directory"
						className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-primary transition-colors"
					>
						<span aria-hidden>←</span>
						Back to Directory
					</Link>
				</div>
			</section>

			<section className="pb-16 sm:pb-24">
				<div className="max-w-screen-lg mx-auto px-4 sm:px-6">
					{!outlet ? (
						<div className="bg-white rounded-2xl shadow-sm p-10 text-center text-gray-500">
							Loading store details…
						</div>
					) : (
						<article className="bg-white rounded-2xl shadow-sm overflow-hidden">
							<div className="relative aspect-[16/9] bg-gradient-to-br from-blue-100 to-indigo-100">
								{outlet.image ? (
									<img
										src={outlet.image}
										alt={outlet.name}
										className="absolute inset-0 w-full h-full object-contain p-8"
									/>
								) : (
									<div className="absolute inset-0 flex items-center justify-center text-6xl">
										🏪
									</div>
								)}
								{outlet.store_category?.name && (
									<span className="absolute top-4 left-4 bg-primary text-white text-xs font-medium uppercase tracking-wide px-3 py-1 rounded-full shadow">
										{outlet.store_category.name}
									</span>
								)}
							</div>

							<div className="p-6 sm:p-10">
								<h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4">
									{outlet.name}
								</h1>

								{outlet.note && (
									<p className="text-gray-700 leading-relaxed mb-8">
										{outlet.note}
									</p>
								)}

								<dl className="grid grid-cols-1 sm:grid-cols-2 gap-6">
									{(outlet.floor || outlet.unit) && (
										<div className="flex items-start gap-3">
											<svg
												className="w-5 h-5 text-primary mt-0.5 flex-shrink-0"
												fill="currentColor"
												viewBox="0 0 20 20"
											>
												<path
													fillRule="evenodd"
													d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
													clipRule="evenodd"
												/>
											</svg>
											<div>
												<dt className="text-xs uppercase tracking-wide text-gray-500 mb-1">
													Location
												</dt>
												<dd className="text-sm text-gray-800">
													{[outlet.floor, outlet.unit].filter(Boolean).join(' · ')}
												</dd>
											</div>
										</div>
									)}

									{outlet.contact_phone && (
										<div className="flex items-start gap-3">
											<svg
												className="w-5 h-5 text-primary mt-0.5 flex-shrink-0"
												fill="currentColor"
												viewBox="0 0 20 20"
											>
												<path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
											</svg>
											<div>
												<dt className="text-xs uppercase tracking-wide text-gray-500 mb-1">
													Phone
												</dt>
												<dd className="text-sm text-gray-800">
													<a
														href={`tel:${outlet.contact_phone}`}
														className="hover:text-primary transition-colors"
													>
														{outlet.contact_phone}
													</a>
												</dd>
											</div>
										</div>
									)}

									{outlet.contact_email && (
										<div className="flex items-start gap-3">
											<svg
												className="w-5 h-5 text-primary mt-0.5 flex-shrink-0"
												fill="currentColor"
												viewBox="0 0 20 20"
											>
												<path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
												<path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
											</svg>
											<div>
												<dt className="text-xs uppercase tracking-wide text-gray-500 mb-1">
													Email
												</dt>
												<dd className="text-sm text-gray-800">
													<a
														href={`mailto:${outlet.contact_email}`}
														className="hover:text-primary transition-colors"
													>
														{outlet.contact_email}
													</a>
												</dd>
											</div>
										</div>
									)}

									{outlet.contact_name && (
										<div className="flex items-start gap-3">
											<svg
												className="w-5 h-5 text-primary mt-0.5 flex-shrink-0"
												fill="currentColor"
												viewBox="0 0 20 20"
											>
												<path
													fillRule="evenodd"
													d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
													clipRule="evenodd"
												/>
											</svg>
											<div>
												<dt className="text-xs uppercase tracking-wide text-gray-500 mb-1">
													Contact Person
												</dt>
												<dd className="text-sm text-gray-800">
													{outlet.contact_name}
												</dd>
											</div>
										</div>
									)}
								</dl>

								<div className="mt-10 pt-6 border-t border-gray-100">
									<Link
										to="/directory"
										className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:border-primary hover:text-primary transition-colors text-sm font-medium"
									>
										← Back to directory
									</Link>
								</div>
							</div>
						</article>
					)}
				</div>
			</section>

			<Footer />
		</div>
	)
}
